import { DEFAULT_COUNTRY, PhoneInput, countryOf } from '@/components/erp/phone-input'
import { useState } from 'react'
import { FileCheck2, Loader2, ShieldCheck, Undo2 } from 'lucide-react'
import { Panel } from '@/components/erp/primitives'
import { Modal } from '@/shared/ui/Modal'
import { ErrorStrip, Note } from '@/shared/ui/states'
import { explain } from '@/shared/api/problem'
import { fmtDateTime } from '@/shared/lib/format'
import type { ConsentResponse } from '@/shared/api/types'
import { CONSENT_METHOD_LABEL, captureConsent, revokeConsent } from './api'

/**
 * The consent artefact — the thing that makes a request legitimate.
 *
 * No request leaves DRAFT without one. That is enforced in the service *and* by
 * a database check constraint, deliberately twice, because the service is where
 * a future refactor forgets.
 *
 * ## The attestation is a legal statement
 *
 * It is stored verbatim and shown verbatim — never truncated, never summarised,
 * never put in a tooltip. It is what the capturer swears to, and a shortened
 * version of a sworn statement is a different statement. The example placeholder
 * is deliberately specific, because a vague attestation is worth nothing:
 *
 * > "I confirm that Jane Wanjiku, ID ending 4471, requested this transfer in my
 * > presence on 14 March 2027 and understood that the transfer is performed by
 * > the IEBC, not by this campaign."
 *
 * Note what the voter is made to understand. That sentence is the difference
 * between recording a request and appearing to move someone.
 */
export function ConsentPanel({
  requestId, consent, canCapture, allowed, onChanged,
}: {
  requestId: string
  consent: ConsentResponse | null | undefined
  canCapture: boolean
  /** `allowedDecisions` from the server. See the note below on why it gates these buttons. */
  allowed: string[]
  onChanged: () => void
}) {
  const [capturing, setCapturing] = useState(false)
  const [revoking, setRevoking] = useState(false)

  const revoked = Boolean(consent?.revokedAt)

  // Both buttons are gated on the SERVER's view of what is legal, never on this
  // component's reading of the status:
  //  · capture is only useful while submission is still ahead — that is exactly
  //    the state in which the server offers SUBMIT;
  //  · revoking auto-withdraws, so it is legal precisely when a withdrawal is.
  // Without this, a RECORDED request — consent present, never revoked — would
  // offer "Revoke" and earn a 409.
  const mayCapture = canCapture && allowed.includes('SUBMIT')
  const mayRevoke = canCapture && allowed.includes('WITHDRAW')

  return (
    <Panel
      title="Consent"
      description={consent ? 'Captured before submission, as required' : 'Required before this can be submitted'}
      right={
        mayCapture && !consent ? (
          <button type="button" className="erp-btn erp-btn-primary" onClick={() => setCapturing(true)}>
            <FileCheck2 className="h-3.5 w-3.5" /> Capture consent
          </button>
        ) : mayRevoke && consent && !revoked ? (
          <button type="button" className="erp-btn" onClick={() => setRevoking(true)}>
            <Undo2 className="h-3.5 w-3.5" /> Revoke
          </button>
        ) : undefined
      }
    >
      {!consent && (
        <Note tone="info">
          This request cannot be submitted until a consent artefact exists. Capture what the voter
          agreed to, how, and who witnessed it.
        </Note>
      )}

      {consent && (
        <div className="space-y-3">
          {revoked && (
            <Note tone="bad">
              Consent was revoked {fmtDateTime(consent.revokedAt!)}
              {consent.revokedReason ? ` — ${consent.revokedReason}` : ''}. Revocation withdraws the
              request automatically; the artefact stays on the record rather than being deleted.
            </Note>
          )}

          <dl className="grid gap-x-4 gap-y-1.5 sm:grid-cols-2 text-erp-sm">
            <Row label="Method" value={CONSENT_METHOD_LABEL[consent.method] ?? consent.method} />
            <Row label="Captured" value={fmtDateTime(consent.capturedAt)} />
            {consent.otpReference && <Row label="OTP reference" value={consent.otpReference} mono />}
            {consent.witnessName && <Row label="Witness" value={consent.witnessName} />}
            {consent.witnessPhone && <Row label="Witness phone" value={consent.witnessPhone} mono />}
            {consent.witnessIdRef && <Row label="Witness ID" value={consent.witnessIdRef} mono />}
            {consent.capturedDevice && <Row label="Device" value={consent.capturedDevice} />}
            {consent.latitude != null && consent.longitude != null && (
              <Row label="Where" value={`${consent.latitude.toFixed(5)}, ${consent.longitude.toFixed(5)}`} mono />
            )}
            {consent.voterConsentId && (
              <Row label="Voter consent record" value="Written to the voter's own record" />
            )}
          </dl>

          <div>
            <div className="text-erp-xs text-muted-foreground mb-1 inline-flex items-center gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5" /> Attestation, verbatim
            </div>
            <blockquote className="text-erp-sm border-l-2 border-chocolate/40 pl-3 py-1 whitespace-pre-wrap">
              {consent.attestation}
            </blockquote>
          </div>
        </div>
      )}

      {capturing && (
        <CaptureModal
          requestId={requestId}
          onClose={() => setCapturing(false)}
          onSaved={() => { setCapturing(false); onChanged() }}
        />
      )}
      {revoking && consent && (
        <RevokeModal
          requestId={requestId}
          onClose={() => setRevoking(false)}
          onDone={() => { setRevoking(false); onChanged() }}
        />
      )}
    </Panel>
  )
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-baseline gap-2 min-w-0">
      <dt className="text-erp-xs text-muted-foreground w-28 shrink-0">{label}</dt>
      <dd className={mono ? 'font-mono text-erp-xs truncate' : 'truncate'}>{value}</dd>
    </div>
  )
}

const METHODS = [
  { code: 'SIGNATURE', label: 'Signature', hint: 'They signed, on paper or on a device.' },
  { code: 'OTP', label: 'One-time code', hint: 'A code sent to their phone was returned.' },
  { code: 'VERBAL_WITNESSED', label: 'Verbal, witnessed', hint: 'They agreed aloud with a named witness present.' },
  { code: 'WEB_FORM', label: 'Web form', hint: 'They submitted it themselves.' },
  { code: 'USSD', label: 'USSD', hint: 'Confirmed through a USSD session.' },
  { code: 'LETTER', label: 'Letter', hint: 'A written instruction — typically a bulk or dissolution case.' },
]

const ATTESTATION_MAX = 4096

function CaptureModal({
  requestId, onClose, onSaved,
}: {
  requestId: string
  onClose: () => void
  onSaved: () => void
}) {
  const [methodCode, setMethodCode] = useState('SIGNATURE')
  const [attestation, setAttestation] = useState('')
  const [otpReference, setOtpReference] = useState('')
  const [witnessName, setWitnessName] = useState('')
  /*
   * The witness number and its country, through the shared picker.
   *
   * It used to be a free-text box that never sent a country at all — and
   * `TransferConsentService` only normalises to E.164 when a country arrives, so every
   * witness number captured from the console was stored exactly as typed. The comment above
   * that branch says the point is "a witness number recorded on two transfers is the same
   * string on both"; `0722114477`, `+254722114477` and `0722 114 477` are three strings for
   * one person, so it was not.
   */
  const [witnessPhone, setWitnessPhone] = useState('')
  const [witnessCountry, setWitnessCountry] = useState(DEFAULT_COUNTRY)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const method = METHODS.find((m) => m.code === methodCode)
  const tooLong = attestation.length > ATTESTATION_MAX
  const ready = attestation.trim().length >= 20 && !tooLong

  return (
    <Modal
      open
      onClose={onClose}
      title="Capture consent"
      description="What the voter agreed to, how you captured it, and what you attest to. This is written to the voter's own consent record as well as to this request."
      footer={
        <>
          <button type="button" className="erp-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button
            type="button"
            className="erp-btn erp-btn-primary"
            disabled={!ready || saving}
            onClick={async () => {
              setSaving(true); setError(null)
              try {
                await captureConsent(requestId, {
                  methodCode,
                  attestation: attestation.trim(),
                  otpReference: otpReference.trim() || null,
                  witnessName: witnessName.trim() || null,
                  witnessPhone: witnessPhone || null,
                  witnessPhoneCountry: witnessPhone
                    ? countryOf(witnessPhone, witnessCountry) : null,
                })
                onSaved()
              } catch (e) { setError(explain(e)); setSaving(false) }
            }}
          >
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Capture
          </button>
        </>
      }
    >
      <ErrorStrip message={error} />

      <div className="space-y-3">
        <label className="block">
          <span className="text-erp-xs text-muted-foreground">How was it captured?</span>
          <select
            className="erp-input w-full mt-1"
            value={methodCode}
            onChange={(e) => setMethodCode(e.target.value)}
          >
            {METHODS.map((m) => <option key={m.code} value={m.code}>{m.label}</option>)}
          </select>
          {method && <p className="text-erp-xs text-muted-foreground mt-1">{method.hint}</p>}
        </label>

        {methodCode === 'OTP' && (
          <label className="block">
            <span className="text-erp-xs text-muted-foreground">OTP reference</span>
            <input className="erp-input w-full mt-1 font-mono text-erp-xs"
              value={otpReference} onChange={(e) => setOtpReference(e.target.value)} />
          </label>
        )}

        {methodCode === 'VERBAL_WITNESSED' && (
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-erp-xs text-muted-foreground">Witness name</span>
              <input className="erp-input w-full mt-1"
                value={witnessName} onChange={(e) => setWitnessName(e.target.value)} />
            </label>
            <label className="block">
              <span className="text-erp-xs text-muted-foreground">Witness phone</span>
              <span className="mt-1 block">
                <PhoneInput
                  value={witnessPhone}
                  onChange={setWitnessPhone}
                  country={witnessCountry}
                  onCountryChange={setWitnessCountry}
                />
              </span>
            </label>
          </div>
        )}

        <label className="block">
          <span className="text-erp-xs text-muted-foreground">
            Attestation <span className="text-red-600">*</span>
          </span>
          <textarea
            className="erp-input w-full mt-1 min-h-[110px]"
            value={attestation}
            onChange={(e) => setAttestation(e.target.value)}
            placeholder="I confirm that Jane Wanjiku, ID ending 4471, requested this transfer in my presence on 14 March 2027 and understood that the transfer is performed by the IEBC, not by this campaign."
          />
          <div className="flex items-start justify-between gap-3 mt-1">
            <p className="text-erp-xs text-muted-foreground">
              Stored word for word and never shortened. Say who asked, when, in whose presence, and
              that they understood the IEBC performs the transfer — not this campaign.
            </p>
            <span className={`text-erp-xs shrink-0 tabular-nums ${tooLong ? 'text-red-600' : 'text-muted-foreground'}`}>
              {attestation.length}/{ATTESTATION_MAX}
            </span>
          </div>
          {tooLong && (
            <p className="text-erp-xs text-red-600 mt-1">
              Too long for the voter's consent record. Shorten it yourself rather than letting it be
              cut — a truncated attestation is a different statement.
            </p>
          )}
        </label>
      </div>
    </Modal>
  )
}

function RevokeModal({
  requestId, onClose, onDone,
}: {
  requestId: string
  onClose: () => void
  onDone: () => void
}) {
  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  return (
    <Modal
      open
      onClose={onClose}
      title="Revoke consent"
      description="Use this when the voter changes their mind, or when the consent turns out not to have been validly given."
      footer={
        <>
          <button type="button" className="erp-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button
            type="button"
            className="erp-btn erp-btn-primary"
            disabled={reason.trim().length < 5 || saving}
            onClick={async () => {
              setSaving(true); setError(null)
              try { await revokeConsent(requestId, reason.trim()); onDone() }
              catch (e) { setError(explain(e)); setSaving(false) }
            }}
          >
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Revoke
          </button>
        </>
      }
    >
      <ErrorStrip message={error} />
      <div className="mb-3">
        <Note tone="bad">
          This withdraws the request automatically. Both the consent and the withdrawal stay on the
          record — a revocation is a second entry, never an erasure of the first.
        </Note>
      </div>
      <label className="block">
        <span className="text-erp-xs text-muted-foreground">Reason</span>
        <textarea className="erp-input w-full mt-1 min-h-[64px]"
          value={reason} onChange={(e) => setReason(e.target.value)} />
      </label>
    </Modal>
  )
}
